import type { JSX, ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import {
	clearActiveMembership,
	storeActiveMembership,
} from 'pkg/actions/memberships';

import type * as models from 'pkg/api/models';
import { RoleType, type Capabilities } from 'pkg/api/models/membership';
import { normalizeMembership } from 'pkg/api/models/membership/utils';
import useMixedState from 'pkg/hooks/useMixedState';
import {
	useAccountIdentity,
	type OrganizationsLocalStorageData,
} from 'pkg/identity/account';
import { isFamilyModeEligible } from 'pkg/identity/family';
import { useOrganizationIdentity } from 'pkg/identity/organization';
import { getJsonItem, LocalStorageKeys } from 'pkg/local_storage';
import * as routes from 'pkg/router/routes';
import { replaceState } from 'pkg/router/state';

import type { CalendarFilterState } from 'routes/group/calendar/filters';

const NoOp = (): void => null;

interface InheritedMembership {
	userId: number;
	role: RoleType;
	capabilities: Capabilities[];
}

interface CurrentMembershipContext {
	membership: Nullable<models.membership.Membership>;
	/**
	 * Sets the membership to the club lobby membership, which enables navigation
	 * to the club lobby.
	 */
	setClubLobbyMembership?: () => void;
	/**
	 * Sets the membership to family mode, where a parent sees all their
	 * children at once. Falls back to the club lobby when the user has fewer
	 * than two children with groups in the organization.
	 */
	setFamilyMembership?: () => void;
	/**
	 * Sets the membership to a given group.
	 * Set targetUserId to activate as a "parent".
	 * Set inheritedMembership to inherit role/capabilities, for users that are admins
	 * higher up in the org structure.
	 */
	setGroupMembership?: (
		groupId: number,
		targetUserId?: number,
		inheritedMembership?: InheritedMembership
	) => void;
}

const DefaultIdentity: CurrentMembershipContext = {
	membership: null,
	setGroupMembership: NoOp,
	setClubLobbyMembership: NoOp,
	setFamilyMembership: NoOp,
};

export const IdentityContext =
	createContext<CurrentMembershipContext>(DefaultIdentity);

let __currentIdentityContextValues: Readonly<Partial<CurrentMembershipContext>>;

/**
 *	@warn This is is an "unsafe" way of getting current context values, and is *only* used in router middlewares.
 */
export function getCurrentIdentityContextValues(): Readonly<
	Partial<CurrentMembershipContext>
> {
	return __currentIdentityContextValues;
}

export function useCurrentMembershipContext(): CurrentMembershipContext {
	return useContext(IdentityContext);
}

interface ProviderProps {
	children: ReactNode;
}

export function CurrentMembershipProvider({
	children,
}: ProviderProps): JSX.Element {
	const [state, setState] =
		useMixedState<CurrentMembershipContext>(DefaultIdentity);

	const [checkedStoredMembership, setCheckedStoredMembership] = useState(false);
	const user = useCurrentUser();
	const { wards } = useOrganizationIdentity();

	// We've changed organizations, see if we have a membership in local storage
	useEffect(() => {
		setCheckedStoredMembership(false);
		if (!user?.id) {
			setCheckedStoredMembership(true);
			return;
		}

		const lsData =
			getJsonItem<OrganizationsLocalStorageData>(
				LocalStorageKeys.Organizations
			) || {};

		const fallback = () => {
			if (user.groups?.length > 0) {
				setGroupMembership(user.groups[0].groupId);
			} else if (wards.length > 0) {
				const childGroups = wards.map((w) => w.groups || []).flat();
				if (childGroups.length > 0) {
					const g = childGroups[0];
					setGroupMembership(g.groupId, g.userId);
				} else {
					setClubLobbyMembership();
				}
			} else {
				setClubLobbyMembership();
			}

			setCheckedStoredMembership(true);
			return;
		};

		// Nothing stored for this organization
		if (!lsData[user.organizationId]?.activeMembership) {
			return fallback();
		}

		const lastMembership = normalizeMembership(
			lsData[user.organizationId].activeMembership
		);
		if (lastMembership.isFamilyMembership) {
			setFamilyMembership();
		} else if (lastMembership.isOrganizationMembership) {
			setClubLobbyMembership();
		} else {
			if (!lastMembership.groupId || !lastMembership.userId) {
				return fallback();
			}

			const inherited = lastMembership.isInherited
				? {
						userId: lastMembership.userId,
						role: lastMembership.role,
						capabilities: lastMembership.capabilities,
					}
				: undefined;

			setGroupMembership(
				lastMembership.groupId,
				lastMembership.targetUserId,
				inherited
			);
		}

		setCheckedStoredMembership(true);
	}, [user?.id]);

	const setMembership = (membership: models.membership.Membership) => {
		storeActiveMembership(user.organizationId, membership);

		membership.user = user;
		if (membership.targetUserId) {
			membership.targetUser = wards.find(
				(u) => u.id === membership.targetUserId
			);
		}

		const nextState: Partial<CurrentMembershipContext> = {
			membership,
		};

		const localStorageFilters = localStorage.getItem('calendarFilters');
		const localFilters: CalendarFilterState = JSON.parse(
			localStorage.getItem('calendarFilters')
		);

		if (localStorageFilters !== null) {
			localFilters.groups = [];
			localStorage.setItem('calendarFilters', JSON.stringify(localFilters));
		}

		setState(nextState);
	};

	const setGroupMembership = (
		groupId: number,
		targetUserId?: number,
		inheritedMembership?: InheritedMembership
	): void => {
		let membership: models.membership.Membership;

		if (!!targetUserId) {
			const w = wards.find((ward) => ward.id === targetUserId);
			if (!w) {
				clearActiveMembership(user.organizationId);
				replaceState(routes.Home());
				return null;
			}
			const wg = w.groups?.find((g) => g.groupId === groupId);
			if (!wg) {
				clearActiveMembership(user.organizationId);
				replaceState(routes.Home());
				return null;
			}

			const userId = user?.id;

			membership = {
				id: 0,
				role: wg.role,
				groupId: groupId,
				userId,
				targetUserId,
				isLegalGuardian: true,
				status: 1,
				capabilities: wg.capabilities,
			};
		} else if (inheritedMembership) {
			// Inherited role – User has a role higher up in the node that can be inherited
			membership = {
				id: 0,
				groupId: groupId,
				userId: inheritedMembership.userId,
				isInherited: true,
				status: 1,
				role: inheritedMembership.role,
				capabilities: inheritedMembership.capabilities,
			};
		} else {
			// Direct member - User has a direct membership, targetUser is 0, inherit is not set
			membership = user.groups?.find((m) => m.groupId === groupId);
			if (!membership) {
				clearActiveMembership(user.organizationId);
				replaceState(routes.Home());
				return null;
			}
		}

		setMembership(membership);
	};

	const setClubLobbyMembership = () => {
		setMembership({
			id: 0,
			role: RoleType.Player,
			status: 1,
			capabilities: [],
			groupId: user.organizationId,
			userId: user.id,
			isOrganizationMembership: true,
		});
	};

	const setFamilyMembership = () => {
		if (!isFamilyModeEligible(wards)) {
			setClubLobbyMembership();
			return;
		}

		setMembership({
			id: 0,
			role: RoleType.Player,
			status: 1,
			capabilities: [],
			groupId: user.organizationId,
			userId: user.id,
			isOrganizationMembership: true,
			isFamilyMembership: true,
		});
	};

	const providedValue: CurrentMembershipContext = {
		...state,
		setClubLobbyMembership,
		setFamilyMembership,
		setGroupMembership,
	};

	__currentIdentityContextValues = Object.freeze(providedValue);

	if (!checkedStoredMembership) {
		return null;
	}

	return (
		<IdentityContext.Provider value={providedValue}>
			{children}
		</IdentityContext.Provider>
	);
}

/* "Selector" helpers */

export function useCurrentUser(): models.user.User {
	const { user } = useOrganizationIdentity();

	return user;
}

export function useCurrentAccount(): models.account.Account {
	const { account } = useAccountIdentity();

	return account;
}

export function useCurrentAccountUserIds(): number[] {
	const { user } = useOrganizationIdentity();

	return user ? [user.id] : [];
}

export function useCurrentMembership(): models.membership.Membership {
	const user = useCurrentUser();
	const identity = useCurrentMembershipContext();

	const membership = identity.membership || user?.groups?.[0];
	if (!membership) {
		return {} as models.membership.Membership;
	}

	return membership;
}

export function useCurrentMemberships(): models.membership.Membership[] {
	return useCurrentUser()?.groups || [];
}

export function useCurrentOrganization(): models.group.Group {
	const { organization } = useAccountIdentity();

	return organization;
}

export function useCurrentAccountWards(): models.account.Account[] {
	const { children } = useAccountIdentity();

	if (!children) {
		return [];
	}

	return children;
}

export function useFetchIdentity(): () => Promise<void> {
	const { refreshAccountIdentity } = useAccountIdentity();
	const { refreshOrganizationIdentity } = useOrganizationIdentity();

	const refreshIdentityProviders = async () => {
		await refreshAccountIdentity();
		await refreshOrganizationIdentity();
	};

	return refreshIdentityProviders;
}
